import { useState } from "react";
import { useRoute, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useStations } from "@/hooks/useStations";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useToast } from "@/hooks/use-toast";
import WorkbenchLayout from "@/components/layout/workbench-layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, ArrowRight, Globe, Mail, ClipboardList, MessageSquare, LayoutDashboard,
  Copy as CopyIcon, Check, ChevronDown, ChevronUp, RefreshCw, Loader2,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import CnaEditor from "@/components/workbench/cna-editor";
import VbrTool from "@/components/workbench/vbr-tool";
import CopyTool from "@/components/workbench/copy-tool";
import type { Sponsor, VbrRequest, CopyMessage, CnaAnalysis } from "@shared/schema";

interface SponsorActivity {
  vbrs: VbrRequest[];
  copies: CopyMessage[];
  cnas: CnaAnalysis[];
}

// Render a stored VBR result (vbr_requests.result jsonb) defensively.
function VbrResultView({ result }: { result: any }) {
  const vbrText: string = result?.vbrText ?? "";
  const citations: any[] = Array.isArray(result?.citations) ? result.citations : [];
  return (
    <div className="space-y-3">
      <p className="text-[15px] leading-relaxed whitespace-pre-wrap text-gray-900">{vbrText || "No stored output."}</p>
      {citations.length > 0 && (
        <div className="border-t border-gray-200 pt-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Citations</p>
          <ul className="text-xs text-gray-500 space-y-1">
            {citations.map((c, i) => (
              <li key={i}>• {typeof c === "string" ? c : (c?.text ?? c?.source ?? JSON.stringify(c))}</li>
            ))}
          </ul>
        </div>
      )}
      {result?.modelInfo?.modelUsed && (
        <p className="text-xs text-gray-400">Model: {result.modelInfo.modelUsed}</p>
      )}
    </div>
  );
}

// Render a saved copy chat session (copy_messages.chat_history) as chat
// bubbles. Every assistant message gets its own copy button.
function ChatTranscript({ messages, idPrefix }: { messages: { role: "user" | "assistant"; text: string }[]; idPrefix: string }) {
  return (
    <div className="space-y-3">
      {messages.map((m, i) =>
        m.role === "user" ? (
          <div key={i} className="flex justify-end">
            <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-emerald-600 text-white px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap">
              {m.text}
            </div>
          </div>
        ) : (
          <div key={i} className="flex items-start gap-1.5">
            <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-white border border-gray-200 px-4 py-2.5 text-[15px] leading-relaxed whitespace-pre-wrap text-gray-900">
              {m.text}
            </div>
            <CopyToClipboard text={m.text} id={`${idPrefix}-msg-${i}`} />
          </div>
        ),
      )}
    </div>
  );
}

// "Regenerate with a different fact": shown under a saved VBR when its
// discovery set was stored (workbench-generated VBRs after this shipped).
// Picking a fact creates a NEW VBR at the top of the history; the original
// stays untouched.
function RegenerateFromFacts({ vbr, sponsorId }: { vbr: VbrRequest; sponsorId: string }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [pendingIdx, setPendingIdx] = useState<number | null>(null);

  const result: any = vbr.result;
  const facts: any[] = Array.isArray(result?.discoveredFacts) ? result.discoveredFacts : [];
  if (facts.length === 0) return null;

  // Prefer the stored input so the regeneration uses the exact original
  // parameters; fall back to what the row itself carries.
  const input = result?.input ?? {
    companyName: vbr.companyName,
    companyWebsite: vbr.websiteUrl || "",
    industry: "",
    targetAudience: "",
    painPoints: "",
    additionalContext: "",
  };

  const regenerate = async (fact: any, idx: number) => {
    setPendingIdx(idx);
    try {
      await apiRequest(`/api/stations/${vbr.stationId}/vbr/regenerate`, "POST", {
        input,
        selectedFact: fact,
        sponsorId,
        allFacts: facts,
      });
      queryClient.invalidateQueries({ queryKey: [`/api/sponsors/${sponsorId}/activity`] });
      toast({ title: "VBR regenerated", description: "The new version was added to the top of this sponsor's history." });
      setOpen(false);
    } catch (err) {
      toast({ title: "Regeneration failed", description: (err as Error).message || "Could not regenerate the VBR", variant: "destructive" });
    } finally {
      setPendingIdx(null);
    }
  };

  const selectedText = result?.selectedFact?.fact ?? result?.selectedFact?.text ?? null;

  return (
    <div className="border-t border-gray-200 pt-3 mt-3">
      <button
        onClick={() => setOpen(!open)}
        className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1.5"
        data-testid={`button-refact-${vbr.id}`}
      >
        <RefreshCw className="h-3.5 w-3.5" />
        Regenerate with a different fact
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            Facts discovered for this VBR - pick one to generate a new version
          </p>
          {facts.map((fact, i) => {
            const text = fact?.fact ?? fact?.text ?? String(fact);
            const isSelected = selectedText != null && text === selectedText;
            return (
              <button
                key={i}
                onClick={() => regenerate(fact, i)}
                disabled={pendingIdx !== null}
                className={`w-full text-left rounded-lg border p-3 transition-all ${
                  isSelected ? "border-blue-200 bg-blue-50/50" : "border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm"
                } ${pendingIdx !== null && pendingIdx !== i ? "opacity-50" : ""}`}
                data-testid={`refact-${vbr.id}-${i}`}
              >
                <div className="flex items-start gap-2.5">
                  {pendingIdx === i ? (
                    <Loader2 className="h-5 w-5 animate-spin shrink-0 mt-0.5 text-blue-600" />
                  ) : (
                    <span className="h-5 w-5 rounded-full bg-blue-50 text-blue-600 text-[10px] font-semibold flex items-center justify-center shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm text-gray-900">{text}</p>
                    {fact?.source?.text && <p className="text-xs text-gray-400 mt-0.5">Source: {fact.source.text}</p>}
                    {isSelected && <p className="text-[11px] text-blue-500 mt-0.5">Used in this VBR</p>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CopyToClipboard({ text, id }: { text: string; id: string }) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Copy failed", description: "Select and copy manually.", variant: "destructive" });
    }
  };
  return (
    <Button variant="ghost" size="sm" onClick={doCopy} data-testid={`button-copy-${id}`}>
      {copied ? <Check className="h-4 w-4 text-green-600" /> : <CopyIcon className="h-4 w-4" />}
    </Button>
  );
}

export default function SponsorWorkbench() {
  const [, params] = useRoute("/workbench/sponsors/:sponsorId");
  const sponsorId = params?.sponsorId ?? "";
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [tab, setTab] = useState("overview");

  const { data: sponsor, isLoading: sponsorLoading } = useQuery<Sponsor>({
    queryKey: [`/api/sponsors/${sponsorId}`],
    enabled: !!sponsorId,
  });

  const { data: activity } = useQuery<SponsorActivity>({
    queryKey: [`/api/sponsors/${sponsorId}/activity`],
    enabled: !!sponsorId,
  });

  // Role-aware station list (admins all, users assigned only), used to
  // resolve station names on history rows.
  const { data: stations = [] } = useStations();

  usePageTitle(sponsor ? `${sponsor.name} - Workbench` : "Sponsor Workbench");

  if (sponsorLoading || !sponsor) {
    return (
      <WorkbenchLayout>
        <Skeleton className="h-10 w-64 mb-6" />
        <Skeleton className="h-40 w-full" />
      </WorkbenchLayout>
    );
  }

  const stationName = (id: string) => stations.find((s) => s.id === id)?.name ?? "Unknown station";
  const vbrs = activity?.vbrs ?? [];
  const copies = activity?.copies ?? [];
  const cnas = activity?.cnas ?? [];

  const websiteHref = sponsor.website
    ? (/^https?:\/\//i.test(sponsor.website) ? sponsor.website : `https://${sponsor.website}`)
    : null;

  const toolCards = [
    {
      id: "vbr",
      title: "VBRs",
      icon: Mail,
      accent: "bg-blue-500",
      iconWrap: "bg-blue-100 text-blue-600",
      count: vbrs.length,
      last: vbrs[0]?.createdAt,
      snippet: (vbrs[0]?.result as any)?.vbrText as string | undefined,
      cta: vbrs.length ? "Open VBRs" : "Generate the first VBR",
    },
    {
      id: "cna",
      title: "Customer Needs Analysis",
      icon: ClipboardList,
      accent: "bg-purple-500",
      iconWrap: "bg-purple-100 text-purple-600",
      count: cnas.length,
      last: cnas[0]?.updatedAt ?? cnas[0]?.createdAt,
      snippet: cnas[0]?.productServiceDescription ?? undefined,
      cta: cnas.length ? "Open CNA" : "Start the CNA",
    },
    {
      id: "copy",
      title: "Copy",
      icon: MessageSquare,
      accent: "bg-emerald-500",
      iconWrap: "bg-emerald-100 text-emerald-600",
      count: copies.length,
      last: copies[0]?.createdAt,
      snippet: copies[0]?.messageContent,
      cta: copies.length ? "Open copy" : "Generate copy",
    },
  ];

  const historyRow = (opts: {
    key: string;
    meta: React.ReactNode;
    actions: React.ReactNode;
    expanded: boolean;
    onToggle: () => void;
    body: React.ReactNode;
    testId: string;
  }) => (
    <div key={opts.key} className="rounded-xl bg-white border border-gray-100 shadow-sm p-4" data-testid={opts.testId}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm min-w-0">{opts.meta}</div>
        <div className="flex items-center gap-1 shrink-0">
          {opts.actions}
          <Button variant="ghost" size="sm" onClick={opts.onToggle}>
            {opts.expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>
      </div>
      {opts.expanded && <div className="mt-3 rounded-lg bg-gray-50 p-5">{opts.body}</div>}
    </div>
  );

  return (
    <WorkbenchLayout>
      {/* Back + sponsor header */}
      <Link href="/workbench" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-4">
        <ArrowLeft className="h-4 w-4" />
        All sponsors
      </Link>

      <div className="mb-6 min-w-0">
        <h1 className="text-3xl font-bold text-gray-900">{sponsor.name}</h1>
        {websiteHref && (
          <a
            href={websiteHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1"
          >
            <Globe className="h-3.5 w-3.5" />
            {sponsor.website}
          </a>
        )}
        {sponsor.notes && <p className="text-sm text-gray-500 mt-2 max-w-2xl">{sponsor.notes}</p>}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview" className="flex items-center gap-1.5">
            <LayoutDashboard className="h-4 w-4" /> Overview
          </TabsTrigger>
          <TabsTrigger value="vbr" className="flex items-center gap-1.5">
            <Mail className="h-4 w-4" /> VBR {vbrs.length > 0 && <Badge variant="secondary" className="ml-1">{vbrs.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="cna" className="flex items-center gap-1.5">
            <ClipboardList className="h-4 w-4" /> CNA {cnas.length > 0 && <Badge variant="secondary" className="ml-1">{cnas.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="copy" className="flex items-center gap-1.5">
            <MessageSquare className="h-4 w-4" /> Copy {copies.length > 0 && <Badge variant="secondary" className="ml-1">{copies.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          {/* Tool summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {toolCards.map((card) => (
              <section
                key={card.id}
                className="relative overflow-hidden rounded-xl bg-white shadow-sm border border-gray-100 p-5 flex flex-col"
              >
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${card.accent}`} />
                <div className="flex items-center gap-2.5 mb-3">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${card.iconWrap}`}>
                    <card.icon className="h-4 w-4" />
                  </div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{card.title}</p>
                </div>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-gray-900">{card.count}</p>
                  <p className="text-xs text-gray-400">
                    {card.last ? `last ${formatDistanceToNow(new Date(card.last), { addSuffix: true })}` : "not used yet"}
                  </p>
                </div>
                {card.snippet && (
                  <p className="text-sm text-gray-500 mt-2 line-clamp-2">{card.snippet}</p>
                )}
                <div className="mt-auto pt-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="px-0 text-gray-600 hover:text-gray-900 hover:bg-transparent"
                    onClick={() => setTab(card.id)}
                    data-testid={`button-open-${card.id}`}
                  >
                    {card.cta} <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </div>
              </section>
            ))}
          </div>
        </TabsContent>

        {/* VBR tool + history */}
        <TabsContent value="vbr" className="mt-6 space-y-4">
          <VbrTool sponsor={sponsor} />
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 pt-3">Past VBRs for {sponsor.name}</p>
          {vbrs.length === 0 ? (
            <div className="text-center py-10 border border-dashed rounded-xl text-gray-400 text-sm">
              No VBRs for this sponsor yet.
            </div>
          ) : (
            vbrs.map((vbr) =>
              historyRow({
                key: vbr.id,
                testId: `vbr-history-${vbr.id}`,
                meta: (
                  <>
                    <span className="font-medium">{stationName(vbr.stationId)}</span>
                    <span className="text-gray-400 ml-2">
                      {vbr.createdAt ? format(new Date(vbr.createdAt), "MMM d, yyyy h:mm a") : ""}
                    </span>
                  </>
                ),
                actions: <CopyToClipboard text={(vbr.result as any)?.vbrText ?? ""} id={`vbr-${vbr.id}`} />,
                expanded: expandedId === vbr.id,
                onToggle: () => setExpandedId(expandedId === vbr.id ? null : vbr.id),
                body: (
                  <>
                    <VbrResultView result={vbr.result} />
                    <RegenerateFromFacts vbr={vbr} sponsorId={sponsor.id} />
                  </>
                ),
              }),
            )
          )}
        </TabsContent>

        {/* CNA */}
        <TabsContent value="cna" className="mt-6">
          <CnaEditor sponsor={sponsor} cnas={cnas} />
        </TabsContent>

        {/* Copy tool + history */}
        <TabsContent value="copy" className="mt-6 space-y-4">
          <CopyTool sponsor={sponsor} />
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 pt-3">Past copy chats for {sponsor.name}</p>
          {copies.length === 0 ? (
            <div className="text-center py-10 border border-dashed rounded-xl text-gray-400 text-sm">
              No copy chats for this sponsor yet.
            </div>
          ) : (
            copies.map((msg) => {
              // Full saved session; rows that predate chat saving show the
              // initial copy as a single-message chat.
              const chat: { role: "user" | "assistant"; text: string }[] =
                Array.isArray(msg.chatHistory) && msg.chatHistory.length > 0
                  ? msg.chatHistory
                  : [{ role: "assistant", text: msg.messageContent }];
              const latestCopy = [...chat].reverse().find((m) => m.role === "assistant")?.text ?? msg.messageContent;
              return historyRow({
                key: msg.id,
                testId: `copy-history-${msg.id}`,
                meta: (
                  <>
                    <span className="font-medium">{stationName(msg.stationId)}</span>
                    <Badge variant="secondary" className="ml-2">{msg.copyType}</Badge>
                    <span className="text-gray-400 ml-2">
                      {msg.createdAt ? format(new Date(msg.createdAt), "MMM d, yyyy h:mm a") : ""}
                    </span>
                    <span className="text-gray-400 ml-2">
                      {chat.length} message{chat.length === 1 ? "" : "s"}
                    </span>
                  </>
                ),
                actions: (
                  <>
                    <span className="text-xs text-gray-400 mr-1">{latestCopy.length} chars</span>
                    {/* Copies the latest assistant message: the most refined copy */}
                    <CopyToClipboard text={latestCopy} id={`copy-${msg.id}`} />
                  </>
                ),
                expanded: expandedId === msg.id,
                onToggle: () => setExpandedId(expandedId === msg.id ? null : msg.id),
                body: <ChatTranscript messages={chat} idPrefix={`copy-${msg.id}`} />,
              });
            })
          )}
        </TabsContent>
      </Tabs>
    </WorkbenchLayout>
  );
}
